import {
  faClipboardCheck,
  faClipboardUser,
} from '@fortawesome/pro-regular-svg-icons';
import {
  ServiceCustomField,
  ServiceCustomFieldDataNumber,
} from '@quality24/inpatient-typings';
import moment from 'moment';
import * as Yup from 'yup';
import { ActiveAlert } from '../../@types/alertMonitors';
import { ActionButtonData } from './types';

/**
 * Validation error messages used by {@link getValidation}. All fields are
 * optional and fall back to the (legacy) Portuguese copy below, so existing
 * callers keep working unchanged.
 */
export interface GetValidationMessages {
  numberTypeError?: string;
  numberMin?: (min: number) => string;
  numberMax?: (max: number) => string;
  invalidEmail?: string;
  invalidDate?: string;
  invalidTime?: string;
  required?: string;
}

const defaultValidationMessages: Required<GetValidationMessages> = {
  numberTypeError: 'O valor deste campo deve ser um número',
  numberMin: (min) => `O valor não pode ser menor que ${min}`,
  numberMax: (max) => `O valor não pode ser maior que ${max}`,
  invalidEmail: 'Insira um email válido',
  invalidDate: 'Data inválida',
  invalidTime: 'Horário inválido',
  required: 'Campo obrigatório',
};

/**
 * Retrieves the correct Yup schema based on the input type
 * @param inputType
 * @param required
 * @param messages Optional overrides for the validation error messages
 *   (e.g. so callers can localize them). Defaults to Portuguese copy for
 *   backward compatibility with existing (pt-BR only) consumers.
 * @returns
 */
export const getValidation = (
  {
    inputType,
    required,
    data,
  }: Pick<
    ServiceCustomField,
    'id' | 'label' | 'inputType' | 'placeholder' | 'required' | 'order' | 'data'
  >,
  messages?: GetValidationMessages,
) => {
  const {
    numberTypeError: numberTypeErrorMessage,
    numberMin: numberMinMessage,
    numberMax: numberMaxMessage,
    invalidEmail: invalidEmailMessage,
    invalidDate: invalidDateMessage,
    invalidTime: invalidTimeMessage,
    required: requiredMessage,
  } = { ...defaultValidationMessages, ...messages };

  let schema;
  if (inputType === 'NUMBER') {
    schema = Yup.number().typeError(numberTypeErrorMessage);
    const complement: ServiceCustomFieldDataNumber = data;
    if (complement.min !== undefined) {
      schema = schema.min(complement.min, numberMinMessage(complement.min));
    }
    if (complement.max !== undefined) {
      schema = schema.max(complement.max, numberMaxMessage(complement.max));
    }
  } else if (inputType === 'EMAIL') {
    schema = Yup.string().email(invalidEmailMessage);
  } else if (inputType === 'DATE') {
    schema = Yup.string().test('min_date', invalidDateMessage, (value) => {
      return moment(value, 'YYYY-MM-DD').isSameOrAfter(moment(), 'date');
    });
  } else if (inputType === 'TIME') {
    schema = Yup.string().test(
      'min_time',
      invalidTimeMessage,
      function test(value) {
        if ('dateRef' in data && data.dateRef && this.parent[data.dateRef]) {
          return moment(
            `${this.parent[data.dateRef]} ${value}`,
            'YYYY-MM-DD HH:mm',
          ).isSameOrAfter(moment());
        }
        return moment(value, 'HH:mm').isSameOrAfter(moment());
      },
    );
  } else {
    schema = Yup.string();
  }

  // Conditionally required field
  // Should test only if schema is a Yup.string()
  if (data.requiredIf && data.requiredIf.length > 0 && inputType !== 'NUMBER') {
    schema = (schema as Yup.StringSchema).test(
      'cond_required',
      requiredMessage,
      function test(value) {
        const shouldCheckRequired =
          data.requiredIf?.every(
            (req) =>
              this.parent[req.fieldId] &&
              req.anyValue.includes(this.parent[req.fieldId]),
          ) || false;
        return shouldCheckRequired ? !!value : true;
      },
    );
  }

  if (required) {
    schema = schema.required(requiredMessage);
  }
  return schema;
};

export const getActionTranslation = (action: string): string[] =>
  ({
    Transferir: ['transferir', 'transferida'],
    Iniciar: ['iniciar', 'iniciada'],
    Finalizar: ['finalizar', 'finalizada'],
  }[action] || ['atualizar', 'atualizada']);

/**
 *
 * Return the next solution step for this alert
 */
export const getAlertNextStep = (alert: ActiveAlert) => {
  const nextSteps = alert.currentStep.nextSteps.nodes
    .filter(({ step }) => step.location !== 'LOCAL')
    .map((n) => n.step);

  const solutionStep = nextSteps.find((n) => n.type === 'SOLUTION');

  const assumingStep = nextSteps.find(
    (n) => n.type === 'ASSUMING_NOTIFICATION',
  );

  // priority order is: solution > assuming > first
  return solutionStep || assumingStep || nextSteps[0];
};

/**
 *
 * Return the alert solution button data for the alert's next step
 */
export const getAlertButtonData = (alert: ActiveAlert) => {
  const step = getAlertNextStep(alert);
  const defaultButtonData: ActionButtonData = {
    solutionStepId: step?.id,
    solutionStepType: step?.type,
    labelPrimary: step?.config?.actionLabel || 'Finalizar',
    labelGrouped: 'Finalizar', // used on grouped buttons >> not implemented yet
    icon: faClipboardCheck,
  };

  if (step?.type === 'SOLUTION') {
    return {
      ...defaultButtonData,
    };
  }

  if (step?.type === 'ASSUMING_NOTIFICATION') {
    return {
      ...defaultButtonData,
      labelPrimary: step?.config?.actionLabel || 'Iniciar',
      labelGrouped: 'Iniciar',
      icon: faClipboardUser,
    };
  }

  return defaultButtonData;
};

/**
 *
 * Return the actions configured for this alert's service and next step
 */
export const getAlertActions = (alert: ActiveAlert) => {
  const nextStep = getAlertNextStep(alert);

  return [
    // Service actions
    ...(alert?.service?.actions?.filter(
      (action) => action.hookType === 'on-before-update',
    ) || []),
    // Step actions
    ...(nextStep?.actions?.filter(
      (action) => action.hookType === 'on-before-update',
    ) || []),
  ];
};
